import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { CalendarItem } from '@tempo/core';
import { IssuesPanel } from '../components/IssuesPanel';
import { ItemSheet } from '../components/ItemSheet';
import { WeekGrid } from '../components/WeekGrid';
import { Button, Loading } from '../components/ui';
import { fromISODate, todayISO } from '../lib/format';
import { useMediaQuery, usePlan, useSettings } from '../lib/hooks';
import { setParams, type Route } from '../lib/router';

export function WeekPage({ route }: { route: Route }) {
  const narrow = useMediaQuery('(max-width: 700px)');
  const settings = useSettings().data;
  const anchor = route.params.get('date') ?? todayISO();
  const count = narrow ? 3 : 7;
  // Desktop: Monday-based weeks. Phone: a rolling 3 days from the anchor.
  const first = narrow ? fromISODate(anchor) : fromISODate(anchor).startOf('week');
  const days = Array.from({ length: count }, (_, i) => first.plus({ days: i }).toISODate()!);
  const plan = usePlan(days[0], count);
  const [selected, setSelected] = useState<CalendarItem | null>(null);
  const go = (delta: number) => setParams({ date: fromISODate(days[0]).plus({ days: delta * count }).toISODate() });
  const label =
    first.month === first.plus({ days: count - 1 }).month
      ? `${first.toFormat('d')}–${first.plus({ days: count - 1 }).toFormat('d LLL')}`
      : `${first.toFormat('d LLL')} – ${first.plus({ days: count - 1 }).toFormat('d LLL')}`;
  const issues = (plan.data?.issues ?? []).filter((i) => i.date && days.includes(i.date) && i.severity !== 'info');

  return (
    <div className="page wide">
      <div className="page-head">
        <h1 className="grow">{label}</h1>
        <Button icon size="sm" onClick={() => go(-1)} aria-label="Previous">
          <ChevronLeft size={18} />
        </Button>
        <Button size="sm" onClick={() => setParams({ date: null })}>
          Today
        </Button>
        <Button icon size="sm" onClick={() => go(1)} aria-label="Next">
          <ChevronRight size={18} />
        </Button>
      </div>
      {issues.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <IssuesPanel issues={issues} compact />
        </div>
      )}
      {plan.isLoading || !settings ? (
        <Loading />
      ) : (
        <WeekGrid days={days} items={plan.data?.items ?? []} dayStart={settings.planner.dayStart} dayEnd={settings.planner.dayEnd} onSelect={setSelected} />
      )}
      {selected && <ItemSheet item={selected} items={plan.data?.items ?? []} onClose={() => setSelected(null)} />}
    </div>
  );
}
